import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, of } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { CodigoSefazEnum } from '@shared/enums/financas/codigo-sefaz.enum';
import { TipoContaEnum } from '@shared/enums/financas/tipo-conta.enum';
import { TipoDestinoEnum } from '@shared/enums/financas/tipo-destino.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { VendaDocumentoService } from '@shared/services/apis/vendas/venda-documento.service';
import { LancarFinanceiroVendaRequest } from '@shared/services/requests/vendas/lancar-financeiro-venda.request';
import { FinanceiroVendaResponse } from '@shared/services/responses/vendas/financeiro-venda.response';
import { ConfigFinancasStruct } from '@shared/services/structs/vendas/config-financas.struct';
import { VendaDetalheStruct } from '@shared/services/structs/vendas/venda-detalhe.struct';
import { VendaPagamentoStruct } from '@shared/services/structs/vendas/venda-pagamento.struct';
import { UtilService } from '@shared/services/util.service';
import { TipoTarifacaoEnum } from '@shared/enums/financas/tipo-tarifacao.enum';
import {
    calcularParcelasIntermediador,
    dataTarifaSeparada,
    resolverCondicao,
} from '../../../venda-documento/components/vd-pagamento/venda-pagamento.intermediador';

export interface PvLancarFinanceiroDialogData {
    idVenda: number;
    /** "Pedido nº 123" — o rótulo do cabeçalho. */
    rotuloDocumento: string;
    /** O documento e o bloco `financas`, quando a tela que abriu já os tem; senão o diálogo busca. */
    venda?: VendaDetalheStruct | null;
    financas?: ConfigFinancasStruct | null;
}

/** O que cada recebimento do pedido vai gerar no Finanças (regra caixa × título do projects/ATS.Hub.API.Financas/context.md). */
export interface PvLancarFinanceiroItemVm {
    sequencia: number;
    forma: string;
    categoria: string;
    conta: string | null;
    valor: number;
    /** true = movimentação de entrada na conta; false = título com parcelas. */
    ehMovimentacao: boolean;
    ehBoleto: boolean;
    ehPix: boolean;
    /** Título: as parcelas como o lançamento grava (T07: vencimento pelo prazo do intermediador, taxa, tarifa, líquido, juros). */
    parcelas: { numero: number; vencimento: string | Date; valor: number; taxa: number | null; tarifa: number | null; liquido: number | null; juros: number | null }[];
    /** Movimentação: uma entrada por parcela, no vencimento de cada uma (à vista é uma só, na data da venda). */
    entradas: { data: string | Date; valor: number }[];
    /** Nome do intermediador quando a forma passa por ele, com a taxa (%) da condição comercial. */
    intermediador: string | null;
    taxaPercentual: number | null;
    /** Tarifa lançada separado (tarifação em dia específico, dia da transação ou dia seguinte). */
    tarifaSeparada: { valor: number; data: Date; conta: string } | null;
    totalJuros: number;
    erro: string | null;
}

/**
 * Lançar financeiro do pedido (T05/T06): mostra o que cada recebimento vai virar no Finanças, pergunta se gera
 * boletos (com o custo de bilhetagem) e chama o orquestrador, que valida tudo de novo. Fecha com `true` quando o
 * financeiro foi lançado — a tela que abriu recarrega o documento.
 */
@Component({
    selector: 'app-pv-lancar-financeiro-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        AlertCardComponent,
        LoadingBoxComponent,
        AbntPipe,
    ],
    templateUrl: './pv-lancar-financeiro-dialog.component.html',
    styleUrls: ['./pv-lancar-financeiro-dialog.component.css'],
})
export class PvLancarFinanceiroDialogComponent implements OnInit {
    public isLoading = true;
    public isLancando = false;

    public itens: PvLancarFinanceiroItemVm[] = [];
    public gerarBoletos = true;
    public qtdParcelasBoleto = 0;
    public custoBoletoUnitario = 0;
    public custoBoletoTotal = 0;

    /** Preenchido depois do lançamento quando há boletos ou links Pix a mostrar: o diálogo vira essa lista. */
    public resultado: FinanceiroVendaResponse | null = null;

    private venda: VendaDetalheStruct | null = null;
    private financas: ConfigFinancasStruct | null = null;

    constructor(
        public dialogRef: MatDialogRef<PvLancarFinanceiroDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: PvLancarFinanceiroDialogData,
        private processoVendaService: ProcessoVendaService,
        private vendaDocumentoService: VendaDocumentoService,
        private alertService: AlertService,
    ) {}

    public ngOnInit(): void {
        this.venda = this.data.venda ?? null;
        this.financas = this.data.financas ?? null;

        // Do board o diálogo só recebe o id: busca o documento e as configurações como a tela do documento faz.
        forkJoin({
            venda: this.venda ? of(null) : this.vendaDocumentoService.getPorId(this.data.idVenda),
            config: this.financas ? of(null) : this.vendaDocumentoService.getConfiguracoes(),
        }).subscribe({
            next: ({ venda, config }) => {
                if (venda) {
                    if (venda.isError || !venda.venda) {
                        this.falharCarregamento(venda.errorDescription || 'Documento não encontrado.');
                        return;
                    }
                    this.venda = venda.venda;
                }
                if (config) {
                    if (config.isError) {
                        this.falharCarregamento(config.errorDescription || 'Não foi possível carregar as configurações do pedido.');
                        return;
                    }
                    this.financas = config.financas ?? null;
                }
                this.montarResumo();
                this.isLoading = false;
            },
            error: () => this.falharCarregamento('Não foi possível carregar o pedido agora. Tente novamente em alguns instantes.'),
        });
    }

    public get lancou(): boolean {
        return this.resultado !== null;
    }

    public get temBoleto(): boolean {
        return this.qtdParcelasBoleto > 0;
    }

    public get temItens(): boolean {
        return this.itens.length > 0;
    }

    /** Algum recebimento com intermediador sem condição comercial ou categoria da tarifa: o orquestrador recusaria. */
    public get temErro(): boolean {
        return this.itens.some((i) => !!i.erro);
    }

    public temTaxa(item: PvLancarFinanceiroItemVm): boolean {
        return item.parcelas.some((p) => p.taxa != null);
    }

    public temTarifaRateada(item: PvLancarFinanceiroItemVm): boolean {
        return item.parcelas.some((p) => (p.tarifa ?? 0) > 0);
    }

    public temJuros(item: PvLancarFinanceiroItemVm): boolean {
        return item.totalJuros > 0;
    }

    public get totalRecebimentos(): number {
        return UtilService.arredondarAbnt(this.itens.reduce((soma, i) => soma + i.valor, 0), 2);
    }

    /** A página pública de cobrança (`/cobranca/:guid`) é desta própria aplicação. */
    public linkCobranca(guid: string): string {
        return `${window.location.origin}/cobranca/${guid}`;
    }

    public copiarLink(guid: string): void {
        navigator.clipboard.writeText(this.linkCobranca(guid)).then(
            () => this.alertService.show('Link copiado', 'Envie o link de cobrança ao cliente.', AlertType.success),
            () => this.alertService.show('Não foi possível copiar', 'Copie o link manualmente.', AlertType.warning),
        );
    }

    public confirmar(): void {
        if (this.isLancando || !this.temItens || this.temErro) return;

        this.isLancando = true;
        const request = new LancarFinanceiroVendaRequest();
        request.gerarBoletos = this.temBoleto && this.gerarBoletos;

        this.processoVendaService.lancarFinanceiro(this.data.idVenda, request).subscribe({
            next: (res) => {
                this.isLancando = false;

                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription || 'Não foi possível lançar o financeiro do pedido.', AlertType.warning);
                    return;
                }

                // Lançou: o retorno vai no toast; o aviso (boleto não gerado, registro no pedido pendente) no de aviso.
                if (res.avisoDescricao) {
                    this.alertService.show('Aviso', res.avisoDescricao, AlertType.warning);
                } else {
                    this.alertService.show('Financeiro lançado', 'O pedido fica em leitura até o financeiro ser excluído.', AlertType.success);
                }
                // À vista, a automação "Ao identificar o pagamento" roda junto; o que não pôde ser executado vem aqui.
                for (const aviso of res.avisosAutomacao ?? []) this.alertService.show('Aviso', aviso, AlertType.warning);

                // Só fica aberto para mostrar os boletos e os links Pix a copiar.
                const boletos = res.boletosGerados;
                const temBoletos = !!boletos && boletos.sucesso + boletos.falha + boletos.semIntegracao > 0;
                if (temBoletos || (res.linksCobranca?.length ?? 0) > 0) {
                    this.resultado = res;
                    return;
                }
                this.dialogRef.close(true);
            },
            error: () => {
                this.isLancando = false;
                this.alertService.show('Aviso', 'Não foi possível lançar o financeiro agora. Tente novamente em alguns instantes.', AlertType.warning);
            },
        });
    }

    public fechar(): void {
        this.dialogRef.close(this.lancou);
    }

    private falharCarregamento(mensagem: string): void {
        this.isLoading = false;
        this.alertService.show('Aviso', mensagem, AlertType.warning);
        this.dialogRef.close(false);
    }

    /**
     * Regra caixa × título, a mesma do orquestrador: destino "Contas a receber" → título; destino de conta com
     * conta Caixa ou Conta bancária → movimentação de entrada; conta de Recebimento/Cartão → título; sem conta
     * (crediário) → título sem conta. No cartão, a conta é a do intermediador quando a tela não gravou.
     */
    private montarResumo(): void {
        if (!this.venda || !this.financas) return;

        const formas = this.financas.listaFormaRecebimento ?? [];
        const contas = this.financas.listaContaFinanceira ?? [];
        const categorias = this.financas.listaCategoria ?? [];
        const intermediadores = this.financas.listaIntermediadorPagamento ?? [];
        const condicoesPagamento = this.financas.listaCondicaoPagamento ?? [];
        const custoUnit = UtilService.arredondarAbnt(this.financas.valorBilhetagemBoleto ?? 0, 2);
        const dataEmissao = this.venda.dataEmissao;
        const dataVenda = new Date(dataEmissao);
        const hoje = new Date();

        this.itens = [...(this.venda.pagamentos ?? [])]
            .sort((a, b) => a.sequencia - b.sequencia)
            .map((pagamento: VendaPagamentoStruct) => {
                const forma = formas.find((f) => f.id === pagamento.idFormaRecebimento) ?? null;
                const codigo = String(forma?.codigoPagamentoSefaz ?? '');
                // T07: nas formas que o intermediador atende (cartão, boleto e Pix por ele), a conta é a dele e ele manda
                // no prazo, na taxa e na tarifa — a mesma regra do orquestrador.
                const formaDeIntermediador = [CodigoSefazEnum.CartaoCredito, CodigoSefazEnum.CartaoDebito, CodigoSefazEnum.Boleto, CodigoSefazEnum.Pix].includes(codigo as CodigoSefazEnum);
                const intermediador = formaDeIntermediador && pagamento.idIntermediadorPagamento
                    ? intermediadores.find((i) => i.idIntermediadorPagamento === pagamento.idIntermediadorPagamento) ?? null
                    : null;
                const idConta = intermediador ? intermediador.idContaFinanceira : pagamento.idContaFinanceira;
                const conta = idConta ? contas.find((c) => c.idContaFinanceira === idConta) ?? null : null;
                const idCategoria = pagamento.idCategoria ?? forma?.idCategoriaFinanceira ?? null;
                const categoria = categorias.find((c) => c.id === idCategoria)?.descricao ?? 'Não informada';

                const ehMovimentacao = !intermediador && !!forma && forma.idTipoDestino !== TipoDestinoEnum.ContasAReceber && !!conta
                    && (conta.idTipoConta === TipoContaEnum.ContaCaixa || conta.idTipoConta === TipoContaEnum.ContaBancaria);
                // À vista (sem parcelas): uma parcela única na data da venda, com o valor do recebimento.
                const base = (pagamento.parcelas ?? []).length > 0
                    ? [...pagamento.parcelas].sort((a, b) => a.numeroParcela - b.numeroParcela)
                        .map((p) => ({ numeroParcela: p.numeroParcela, vencimento: p.dataVencimento as string | Date, valor: p.valor }))
                    : [{ numeroParcela: 1, vencimento: dataEmissao as string | Date, valor: UtilService.arredondarAbnt(pagamento.valor ?? 0, 2) }];

                const condicaoPagamento = pagamento.idCondicaoPagamento
                    ? condicoesPagamento.find((c) => c.idCondicaoPagamento === pagamento.idCondicaoPagamento) ?? null
                    : null;
                const taxaJuros = condicaoPagamento?.taxaJuros ?? 0;
                const juros = (valor: number) => (taxaJuros > 0 ? UtilService.arredondarAbnt(valor * taxaJuros / 100, 2) : null);

                let parcelas: PvLancarFinanceiroItemVm['parcelas'] = [];
                let tarifaSeparada: PvLancarFinanceiroItemVm['tarifaSeparada'] = null;
                let taxaPercentual: number | null = null;
                let erro: string | null = null;

                if (intermediador) {
                    const cond = resolverCondicao(intermediador, forma?.id, pagamento.idIntermediadorBandeira, base.length);

                    if (!cond) {
                        erro = `O intermediador "${intermediador.descricao}" não tem condição comercial para esta forma em ${base.length}x.`;
                    } else {
                        const tarifa = UtilService.arredondarAbnt(cond.tarifa ?? 0, 2);
                        const rateada = tarifa > 0 && intermediador.idTipoTarifacao === TipoTarifacaoEnum.DiaDoPagamento;
                        const calculadas = calcularParcelasIntermediador(base, dataVenda, cond.prazo, cond.taxa ?? 0, rateada ? tarifa : 0, intermediador.considerarProximoDiaUtil);
                        const dataTarifa = tarifa > 0 ? dataTarifaSeparada(intermediador.idTipoTarifacao, intermediador.diaDebito, hoje, intermediador.considerarProximoDiaUtil) : null;

                        taxaPercentual = cond.taxa ?? 0;
                        parcelas = calculadas.map((p) => ({ numero: p.numeroParcela, vencimento: p.vencimento, valor: p.valor, taxa: p.taxa, tarifa: rateada ? p.tarifa : null, liquido: p.liquido, juros: juros(p.valor) }));
                        tarifaSeparada = dataTarifa ? { valor: tarifa, data: dataTarifa, conta: intermediador.descricaoContaFinanceira ?? intermediador.descricao } : null;
                        if (dataTarifa && !intermediador.idCategoriaFinanceiraTarifa) erro = `O intermediador "${intermediador.descricao}" não tem categoria para a tarifa.`;
                    }
                } else if (!ehMovimentacao) {
                    parcelas = base.map((p) => ({ numero: p.numeroParcela, vencimento: p.vencimento, valor: p.valor, taxa: null, tarifa: null, liquido: null, juros: juros(p.valor) }));
                }

                return {
                    sequencia: pagamento.sequencia,
                    forma: forma?.descricao ?? 'Forma não informada',
                    categoria,
                    conta: conta?.descricao ?? null,
                    valor: UtilService.arredondarAbnt(pagamento.valor ?? 0, 2),
                    ehMovimentacao,
                    ehBoleto: !intermediador && !ehMovimentacao && !!conta && codigo === CodigoSefazEnum.Boleto,
                    ehPix: !intermediador && !ehMovimentacao && !!conta && codigo === CodigoSefazEnum.Pix,
                    parcelas,
                    entradas: ehMovimentacao ? base.map((p) => ({ data: p.vencimento, valor: p.valor })) : [],
                    intermediador: intermediador?.descricao ?? null,
                    taxaPercentual,
                    tarifaSeparada,
                    totalJuros: UtilService.arredondarAbnt(parcelas.reduce((s, p) => s + (p.juros ?? 0), 0), 2),
                    erro,
                };
            });

        this.qtdParcelasBoleto = this.itens.filter((i) => i.ehBoleto).reduce((soma, i) => soma + i.parcelas.length, 0);
        this.custoBoletoUnitario = custoUnit;
        this.custoBoletoTotal = UtilService.arredondarAbnt(this.qtdParcelasBoleto * custoUnit, 2);
    }
}
